import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('Le mot de passe doit faire au moins 6 caractères');
    if (password !== confirm) return setError('Les mots de passe ne correspondent pas');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset-confirm`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Lien expiré ou invalide');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="reset-invalid">
        <div className="max-w-md w-full p-8 bg-white border border-slate-200 text-center">
          <Warning size={40} className="mx-auto text-red-500 mb-3" weight="fill" />
          <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
          <p className="text-slate-600 mb-6">Le lien de réinitialisation est incomplet ou corrompu.</p>
          <Link to="/login" className="inline-block bg-[#C75B39] text-white px-6 py-3 font-semibold hover:bg-[#A94B2E]">
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="reset-success">
        <div className="max-w-md w-full p-8 bg-white border border-slate-200 text-center">
          <CheckCircle size={48} className="mx-auto text-emerald-600 mb-3" weight="fill" />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Mot de passe modifié
          </h2>
          <p className="text-slate-600 mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#C75B39] text-white px-6 py-3 font-semibold hover:bg-[#A94B2E]"
            data-testid="btn-go-login"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="reset-form">
      <div className="max-w-md w-full p-8 bg-white border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#C75B39] text-white flex items-center justify-center">
            <Lock size={20} weight="bold" />
          </div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Nouveau mot de passe
          </h2>
        </div>
        <p className="text-slate-600 mb-6">Choisissez un nouveau mot de passe sécurisé pour votre compte.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nouveau mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-slate-300 focus:outline-none focus:border-[#C75B39]"
              placeholder="••••••••"
              required
              data-testid="input-new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full p-3 border border-slate-300 focus:outline-none focus:border-[#C75B39]"
              placeholder="••••••••"
              required
              data-testid="input-confirm-password"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200">
              <Warning size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C75B39] text-white py-3 font-semibold hover:bg-[#A94B2E] disabled:opacity-60"
            data-testid="btn-submit-reset"
          >
            {loading ? 'Modification…' : 'Modifier le mot de passe'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm">
          <Link to="/login" className="text-slate-500 hover:text-slate-900">Retour à la connexion</Link>
        </p>
      </div>
    </div>
  );
}
